import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { EventService, GridService } from '@core/services';
import { remToPx } from '@util/layout';

import { SelectedRow } from '#types/grid';
import { OrderInquiryAttachmentResponseRow } from '#types/order';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@order/constants';
import { OrderService } from '@order/services';
import { OrderActions } from '@order/state';

@Component({
  templateUrl: './inquiry-attachment-dialog.component.html',
  styleUrls: ['./inquiry-attachment-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderInquiryAttachmentDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  loading$: Observable<boolean>;

  lookupId = 'order-inquiry-attachment-dialog';

  selectedRow: SelectedRow<OrderInquiryAttachmentResponseRow> = null;
  downloadButtonDisabled$ = new BehaviorSubject<boolean>(true);

  gridId = CONSTANTS.GRID_ORDER_INQUIRY_ATTACHMENT;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    public orderService: OrderService,
    public ref: DynamicDialogRef,
    private gridService: GridService,
    public config: DynamicDialogConfig,
    private authService: AuthService,
    private ngZone: NgZone,
    private translocoService: TranslocoService,
    private eventService: EventService
  ) {}

  ngOnInit() {
    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.close();
      });

    this.loading$ = this.eventService.loading(CONSTANTS.REQUEST_ORDER_DOWNLOAD_ATTACHMENT);

    this.gridService
      .selectedRow$<OrderInquiryAttachmentResponseRow>(this.gridId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedRow) => {
        this.selectedRow = selectedRow;

        if (!this.selectedRow?.data) {
          this.downloadButtonDisabled$.next(true);
        } else {
          this.downloadButtonDisabled$.next(false);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.orderService.orderInquiryAttachmentDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }
    } catch {}
  }

  close() {
    this.ref.close(undefined);
  }

  downloadAttachment() {
    if (!this.selectedRow?.data) {
      return;
    }

    this.gridService
      .selectedRows$<OrderInquiryAttachmentResponseRow>(this.gridId)
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          this.dispatchAction(data);
        }
      });
  }

  dispatchAction(data: OrderInquiryAttachmentResponseRow[]) {
    for (let i = 0; i < Object.keys(data).length; i++) {
      const item = data[i];

      this.store.dispatch(
        OrderActions.InquiryAttachmentRequestDownload({
          filePath: item.filepath,
        })
      );
    }
  }
}
